import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
// Import styles
import innerStyles from './outline-tabs.inner.pcss?inline';
import globalStyles from './outline-tabs.global.pcss?inline';
import { classMap } from 'lit/directives/class-map.js';

/**
 * Outline Tabs component implementing the ARIA Tabs Pattern with automatic activation
 * @element outline-tabs
 *
 * @todo Designs for this component are not yet finalized. While the component's
 * functionality is complete, the styles are pending design review and implementation.
 *
 * @slot {HTMLButtonElement} tabs - Individual tab buttons. Each button requires:
 *   - slot="tabs"
 *   - role="tab"
 *   - id (unique)
 *   - aria-controls (matching panel id)
 *
 * @slot {HTMLElement} panels - Individual panel elements. Each panel requires:
 *   - slot="panels"
 *   - role="tabpanel"
 *   - id (matching tab's aria-controls)
 *
 * @fires tab-change - Fires when a tab is selected with the tab's ID
 *
 * @property {boolean} debug - When true, enables debug logging of component lifecycle events
 * @property {boolean} useUrlState - When true, enables URL state management for active tab
 * @property {string} instanceId - Unique identifier for this tabs instance, used for URL parameter naming
 * @property {string} linkSelector - CSS selector with optional attribute pattern for links to be enhanced.
 *                                  Format: "selector|attributePattern", where attributePattern supports wildcards (*).
 *                                  Examples:
 *                                  - ".pager__item a" (enhances href attributes only)
 *                                  - "outline-pager-input|*href*" (enhances any attribute containing 'href')
 *                                  - Multiple selectors can be comma-separated
 * @property {boolean} globalLinkScope - When true, enhances links across the entire document
 * @property {boolean} enhanceUrls - When true, enhances links links with tab state
 * @property {string} fadeWidth - Fade width for the scroll indicators on the tablist
 * @property {string} fadeColor - Color for the scroll fade indicators
 * @property {boolean} tabsMobileOnly - When true, the tablist-container is visible only on mobile devices
 * @property {string} variation - Controls the tab variation: "search" (default), "default", or "primary".
 *                               - "search" - scrolling tabs on mobile
 *                               - "default" - dropdown on mobile with brand-secondary active color
 *                               - "primary" - dropdown on mobile with brand-primary active color
 * @property {boolean} responsiveVariation - When true, the component will automatically switch between dropdown and
 * regular tab behavior based on screen size for "default" and "primary" variations.
 * This means on desktop, all variations will behave the same regardless of setting.
 *
 * @state parsedSelectors
 * When useUrlState is enabled, the component:
 * 1. Preserves active tab state in URL parameters
 * 2. Updates matching links to maintain tab state across page loads
 * 3. Supports various URL formats:
 *    - Query strings (e.g., "?page=1")
 *    - Relative paths (e.g., "/path/to/page")
 *    - Full URLs
 * 4. Preserves original URL format when updating
 * 5. Supports flexible attribute matching via patterns
 *
 * Implementation Note:
 * When implementing this component in a CMS or templating system, each tab/panel pair should
 * be created as individual, top-level elements with their respective slots. Do not wrap
 * tab/panel pairs in container elements. The component expects direct access to these
 * elements to manage their state and interactions properly.
 */

@customElement('outline-tabs')
export class OutlineTabs extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  /**
   * Debug mode flag that enables logging of component lifecycle events and state changes.
   * When enabled, the component will output detailed logs about initialization, updates,
   * and internal state changes to help with development and troubleshooting.
   */
  @property({ type: Boolean, attribute: 'debug' })
  debug = false;

  /**
   * When true, the component will manage tab state in URL parameters
   */
  @property({ type: Boolean, attribute: 'use-url-state' })
  useUrlState = false;

  /**
   * Unique identifier for this tabs instance
   * Used to scope URL parameters when multiple tab sets exist
   */
  @property({ type: String, attribute: 'instance-id' })
  instanceId = '';

  /**
   * CSS selector with optional attribute pattern for links to be enhanced.
   * Format: "selector|attributePattern", where attributePattern supports wildcards (*).
   * Examples:
   * - ".pager__item a" (enhances href attributes only)
   * - "outline-pager-input|*href*" (enhances any attribute containing 'href')
   * - Multiple selectors can be comma-separated
   */
  @property({ type: String, attribute: 'link-selector' })
  linkSelector = '.pager__item a, .pager a, outline-pager-input|*href*';

  /**
   * When true, enhances links across the entire document
   * When false, only enhances links within this component
   */
  @property({ type: Boolean, attribute: 'global-link-scope' })
  globalLinkScope = false;

  /**
   * When true, enhances pagination links with tab state
   * When false, pagination links will not be modified
   * This is useful when pagination is handled by JavaScript instead of page navigation
   */
  @property({ type: Boolean, attribute: 'enhance-urls' })
  enhanceUrls = false;

  /**
   * Fade width for the scroll indicators on the tablist
   * Controls the width of the gradient that appears when tabs overflow
   */
  @property({ type: String, attribute: 'fade-width' })
  fadeWidth = '2rem';

  /**
   * Color for the scroll fade indicators
   * Should match the background color where the tabs are displayed
   */
  @property({ type: String, attribute: 'fade-color' })
  fadeColor = 'white';

  /**
   * When true, the tablist-container is visible only on mobile devices
   */
  @property({ type: Boolean, attribute: 'tabs-mobile-only' })
  tabsMobileOnly = false;

  /**
   * Controls the variation of the tab component
   * - "search" (default): Scrolling tabs on mobile
   * - "default" - dropdown on mobile with brand-secondary active color
   * - "primary" - dropdown on mobile with brand-primary active color
   */
  @property({ type: String, attribute: 'variation' })
  variation: 'search' | 'default' | 'primary' = 'search';

  /**
   * When true, the component will automatically switch between dropdown and
   * regular tab behavior based on screen size for "default" and "primary" variations.
   * This means on desktop, all variations will behave the same regardless of setting.
   */
  private get responsiveVariation(): boolean {
    // Return true unless the variation is "search"
    return this.variation !== 'search';
  }

  /**
   * Current selected tab label for mobile dropdown variations
   */
  @state()
  private selectedTabLabel = '';

  /**
   * Flag to track if mobile dropdown is open
   */
  @state()
  private isDropdownOpen = false;

  /**
   * Store the ID of the currently active tab
   */
  @state()
  private activeTabId: string | null = null;

  /**
   * Parsed selector patterns for internal use
   * @private
   */
  @state()
  private parsedSelectors: Array<{
    selector: string;
    attributePattern?: string;
  }> = [];

  /**
   * The URL parameter name used for storing active tab state
   * Includes instance ID if provided
   * @private
   */
  private get urlParamName(): string {
    return this.instanceId ? `tab-state-${this.instanceId}` : 'tab-state';
  }

  /**
   * Arrays and references for managing tab and panel elements
   * @private
   */
  private tabs: HTMLElement[] = [];
  private panels: HTMLElement[] = [];
  private firstTab: HTMLElement | null = null;
  private lastTab: HTMLElement | null = null;

  /**
   * Tracks elements with attached event listeners for cleanup
   * @private
   */
  private elementsWithListeners: WeakMap<HTMLElement, boolean> = new WeakMap();

  /**
   * Observer for dynamically added links
   * @private
   */
  private linkObserver: MutationObserver | null = null;

  /**
   * Reference to the tablist element
   * @private
   */
  private tablistElement: HTMLElement | null = null;

  /**
   * Scroll handling state
   * @private
   */
  private isScrolling = false;
  private scrollTimeout: number | null = null;

  /**
   * Utility methods for URL handling
   * @private
   */
  private urlUtils = {
    /**
     * Determines the type of URL (query string only, relative, absolute, or full URL)
     * @param url - The URL to analyze
     * @returns Object with URL type information
     */
    getUrlType: (
      url: string
    ): {
      isQueryStringOnly: boolean;
      isRelativeUrl: boolean;
      hasProtocol: boolean;
    } => {
      return {
        isQueryStringOnly: url.startsWith('?'),
        isRelativeUrl: !url.includes('://') && !url.startsWith('/'),
        hasProtocol: url.includes('://'),
      };
    },

    /**
     * Creates a URL object from a string, handling various URL formats
     * @param urlString - The URL string to parse
     * @returns URL object
     */
    parseUrl: (urlString: string): URL => {
      return new URL(urlString, window.location.href);
    },

    /**
     * Adds or updates a parameter in a URL
     * @param urlString - The original URL string
     * @param paramName - The parameter name to set
     * @param paramValue - The parameter value to set
     * @returns The modified URL string, preserving the original format
     */
    addOrUpdateUrlParameter: (
      urlString: string,
      paramName: string,
      paramValue: string
    ): string => {
      const urlType = this.urlUtils.getUrlType(urlString);

      // If it's just a query string, handle it directly
      if (urlType.isQueryStringOnly) {
        const params = new URLSearchParams(urlString);
        params.set(paramName, paramValue);
        return '?' + params.toString();
      }

      // For paths or full URLs, use URL API but preserve format
      const url = this.urlUtils.parseUrl(urlString);
      const params = new URLSearchParams(url.search);
      params.set(paramName, paramValue);

      // Preserve the original format
      if (urlType.hasProtocol) {
        // Full URL with protocol
        return url.toString();
      } else if (urlType.isRelativeUrl) {
        // Relative URL - preserve the relative format
        const path = urlString.split('?')[0] || '';
        return (
          path + (params.toString() ? '?' + params.toString() : '') + url.hash
        );
      } else {
        // Absolute path without protocol
        return (
          url.pathname +
          (params.toString() ? '?' + params.toString() : '') +
          url.hash
        );
      }
    },

    /**
     * Creates a new URL with updated search parameters
     * @param paramName - The parameter name to set
     * @param paramValue - The parameter value to set
     * @returns The new URL string
     */
    createUrlWithUpdatedParams: (
      paramName: string,
      paramValue: string
    ): string => {
      const params = new URLSearchParams(window.location.search);
      params.set(paramName, paramValue);
      return `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    },
  };

  /**
   * Utility method for debug logging
   * @private
   */
  private debugLog(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`🔤 [OutlineTabs] ${message}`, ...args);
    }
  }

  /**
   * Gets the current URL parameters as a URLSearchParams object
   * @private
   */
  private getUrlParams(): URLSearchParams {
    return new URLSearchParams(window.location.search);
  }

  /**
   * Updates the URL with the new active tab ID while preserving other parameters
   * @param tabId - The ID of the active tab
   * @private
   */
  private updateUrlState(tabId: string): void {
    if (!this.useUrlState) {
      this.debugLog('Not updating URL state because useUrlState is false');
      return;
    }

    this.debugLog(`Updating URL state with tab: ${tabId}`, {
      useUrlState: this.useUrlState,
      enhanceUrls: this.enhanceUrls,
      urlParamName: this.urlParamName,
    });

    const params = this.getUrlParams();
    const oldParams = new URLSearchParams(params.toString());
    params.set(this.urlParamName, tabId);

    this.debugLog('URL parameters:', {
      before: oldParams.toString(),
      after: params.toString(),
    });

    // Update URL without triggering a page reload
    const oldUrl = window.location.href;
    const newUrl = this.urlUtils.createUrlWithUpdatedParams(
      this.urlParamName,
      tabId
    );
    window.history.pushState({}, '', newUrl);
    this.debugLog(`Updated URL: ${oldUrl} -> ${newUrl}`);

    // Update links with new tab state
    if (this.enhanceUrls) {
      this.debugLog('Enhancing links after URL state update');
      this.enhanceLinks();
    } else {
      this.debugLog(
        'Not enhancing links after URL state update because enhanceUrls is false'
      );
    }
  }

  /**
   * Gets the active tab ID from URL parameters if available
   * @private
   */
  private getActiveTabFromUrl(): string | null {
    if (!this.useUrlState) return null;

    const params = this.getUrlParams();
    const tabId = params.get(this.urlParamName);
    this.debugLog(`Retrieved tab ID from URL: ${tabId}`);
    return tabId;
  }

  /**
   * Sets up mutation observer to watch for dynamically added links
   * @private
   */
  private setupLinkObserver(): void {
    this.debugLog('Setting up link observer', {
      useUrlState: this.useUrlState,
      enhanceUrls: this.enhanceUrls,
      globalLinkScope: this.globalLinkScope,
    });

    if (!this.useUrlState || !this.enhanceUrls) {
      this.debugLog(
        'Skipping link observer setup because URL state or link enhancement is disabled'
      );
      return;
    }

    this.linkObserver = new MutationObserver(mutations => {
      let shouldEnhance = false;
      this.debugLog('Link observer detected mutations:', mutations.length);

      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          this.debugLog('Mutation added nodes:', {
            type: mutation.type,
            addedNodes: mutation.addedNodes.length,
            target: mutation.target,
          });
          shouldEnhance = true;
        }
      });

      if (shouldEnhance) {
        this.debugLog('Mutations triggered link enhancement');
        this.enhanceLinks();
      } else {
        this.debugLog('Mutations did not trigger link enhancement');
      }
    });

    // Observe the appropriate scope
    const scope = this.globalLinkScope ? document.body : this;
    this.debugLog(
      `Observing ${this.globalLinkScope ? 'document.body' : 'component'} for link changes`
    );

    this.linkObserver.observe(scope, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Handles changes to properties
   * @param changedProperties - Map of changed properties
   */
  protected updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);

    this.debugLog('Component updated, changed properties:', {
      properties: Array.from(changedProperties.keys()),
      useUrlState: this.useUrlState,
      enhanceUrls: this.enhanceUrls,
    });

    // Update CSS variables when fade properties change
    if (
      changedProperties.has('fadeColor') ||
      changedProperties.has('fadeWidth')
    ) {
      this.debugLog('Fade properties changed', {
        fadeColor: this.fadeColor,
        fadeWidth: this.fadeWidth,
      });
      this.style.setProperty('--fade-color', this.fadeColor);
      this.style.setProperty('--fade-width', this.fadeWidth);
    }

    // If variation changes, we need to handle media query listeners
    // since responsiveVariation is derived from variation
    if (changedProperties.has('variation')) {
      this.debugLog(
        'Variation changed, updating responsiveVariation behavior',
        {
          variation: this.variation,
          responsiveVariation: this.responsiveVariation,
          activeTabId: this.activeTabId,
        }
      );

      const mediaQuery = window.matchMedia('(max-width: 992px)');
      // If we're becoming responsive now
      if (this.responsiveVariation) {
        // Remove any existing listener
        mediaQuery.removeEventListener(
          'change',
          this.boundHandlers.mediaQueryChange
        );
        mediaQuery.addEventListener(
          'change',
          this.boundHandlers.mediaQueryChange
        );
        // Trigger an immediate check
        this.handleMediaQueryChange({
          matches: mediaQuery.matches,
        } as MediaQueryListEvent);
      } else {
        // No longer responsive, remove listener if it was there
        mediaQuery.removeEventListener(
          'change',
          this.boundHandlers.mediaQueryChange
        );
      }

      // Force a re-render to update layout
      this.requestUpdate();

      // Use setTimeout to ensure DOM has updated before checking overflow
      setTimeout(() => {
        this.checkTablistOverflow();
      }, 100);
    }

    if (changedProperties.has('linkSelector')) {
      this.debugLog('Link selector changed, updating parsed selectors');
      this.parseSelectors();
      if (this.useUrlState) {
        this.debugLog('Calling enhanceLinks due to linkSelector change');
        this.enhanceLinks();
      } else {
        this.debugLog('Not calling enhanceLinks because useUrlState is false');
      }
    }

    if (
      changedProperties.has('useUrlState') ||
      changedProperties.has('enhanceUrls')
    ) {
      this.debugLog('URL state or enhance URLs setting changed', {
        useUrlState: this.useUrlState,
        enhanceUrls: this.enhanceUrls,
        hadLinkObserver: !!this.linkObserver,
      });

      // Clean up existing observer if it exists
      if (this.linkObserver) {
        this.debugLog('Disconnecting existing link observer');
        this.linkObserver.disconnect();
        this.linkObserver = null;
      }

      // Set up new observer if needed
      if (this.useUrlState && this.enhanceUrls) {
        this.debugLog('Setting up new link observer');
        this.setupLinkObserver();
      }
    }

    // If activeTabId changed, ensure the correct tab is selected
    if (changedProperties.has('activeTabId') && this.activeTabId) {
      // Find the tab with the active ID
      const activeTab = this.tabs.find(tab => tab.id === this.activeTabId);
      if (activeTab && activeTab.getAttribute('aria-selected') !== 'true') {
        // If tab exists and is not already selected, select it
        this.debugLog(`Setting active tab after update: ${this.activeTabId}`);
        this.setSelectedTab(activeTab, false);
      }
    }
  }

  /**
   * Parse and cache the link selectors
   * @private
   */
  private parseSelectors(): void {
    this.parsedSelectors = this.linkSelector.split(',').map(part => {
      const [selector, attributePattern] = part.trim().split('|');
      return {
        selector: selector.trim(),
        attributePattern: attributePattern?.trim(),
      };
    });

    this.debugLog('Parsed selectors:', this.parsedSelectors);
  }

  /**
   * Enhances links with the current tab state
   * @private
   */
  private enhanceLinks(): void {
    this.debugLog('enhanceLinks called with state:', {
      useUrlState: this.useUrlState,
      enhanceUrls: this.enhanceUrls,
      globalLinkScope: this.globalLinkScope,
      linkSelector: this.linkSelector,
    });

    if (!this.useUrlState || !this.enhanceUrls) {
      this.debugLog('Skipping link enhancement because:', {
        useUrlState: this.useUrlState ? 'true' : 'false',
        enhanceUrls: this.enhanceUrls ? 'true' : 'false',
        condition: `${!this.useUrlState ? 'useUrlState is false' : ''} ${!this.enhanceUrls ? 'enhanceUrls is false' : ''}`,
      });
      return;
    }

    const currentTab = this.tabs.find(
      tab => tab.getAttribute('aria-selected') === 'true'
    );
    if (!currentTab) {
      this.debugLog('No current tab found, skipping link enhancement');
      return;
    }

    // Parse selectors if not already done
    if (!this.parsedSelectors.length) {
      this.parseSelectors();
    }

    // Determine the scope for link enhancement
    const scope = this.globalLinkScope ? document : this;

    this.debugLog('Enhancing links', {
      currentTab: currentTab.id,
      enhanceUrls: this.enhanceUrls,
      globalLinkScope: this.globalLinkScope,
      scope: this.globalLinkScope ? 'document' : 'component',
    });

    // Handle each selector separately
    this.parsedSelectors.forEach(({ selector, attributePattern }) => {
      this.debugLog(
        `Processing selector: ${selector}${attributePattern ? ` | ${attributePattern}` : ''}`
      );

      try {
        const elements = scope.querySelectorAll(selector);
        this.debugLog(
          `Found ${elements.length} elements matching selector: ${selector}`
        );

        elements.forEach(element => {
          if (attributePattern) {
            // If an attribute pattern is specified, enhance matching attributes
            Array.from(element.attributes).forEach(attr => {
              if (this.matchesPattern(attr.name, attributePattern)) {
                this.debugLog(
                  `Enhancing attribute ${attr.name} on element:`,
                  element
                );
                this.enhanceLinkAttribute(element, attr.name, currentTab.id);
              }
            });
          } else {
            // Otherwise, enhance the href attribute by default
            if (element.hasAttribute('href')) {
              this.debugLog(`Enhancing href attribute on element:`, element);
              this.enhanceLinkAttribute(element, 'href', currentTab.id);
            }
          }
        });
      } catch (error) {
        this.debugLog(`Error processing selector ${selector}: ${error}`);
      }
    });

    this.debugLog('Enhanced links with tab state');
  }

  /**
   * Checks if an attribute name matches a pattern with wildcards
   * @private
   * @param {string} attributeName - The attribute name to check
   * @param {string} pattern - The pattern to match against (can include wildcards)
   * @returns {boolean} - Whether the attribute name matches the pattern
   */
  private matchesPattern(attributeName: string, pattern: string): boolean {
    const regexPattern = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
    const matches = regexPattern.test(attributeName);
    this.debugLog(
      `Matching attribute ${attributeName} against pattern ${pattern}: ${matches ? 'MATCH' : 'NO MATCH'}`
    );
    return matches;
  }

  /**
   * Enhances a link attribute with the current tab state
   * @private
   * @param {Element} element - The element to enhance
   * @param {string} attributeName - The attribute name to enhance
   * @param {string} tabId - The ID of the current tab
   */
  private enhanceLinkAttribute(
    element: Element,
    attributeName: string,
    tabId: string
  ): void {
    try {
      const originalValue = element.getAttribute(attributeName);
      if (!originalValue) {
        this.debugLog(
          `Attribute ${attributeName} has no value, skipping enhancement`
        );
        return;
      }

      this.debugLog(
        `Enhancing attribute ${attributeName} with original value: ${originalValue}`
      );

      // Add or update the tab state parameter in the URL
      const newValue = this.urlUtils.addOrUpdateUrlParameter(
        originalValue,
        this.urlParamName,
        tabId
      );

      // Set the updated attribute value
      element.setAttribute(attributeName, newValue);

      // Log the URL type for debugging
      const urlType = this.urlUtils.getUrlType(originalValue);
      let urlTypeStr = 'absolute path'; // default

      if (urlType.isQueryStringOnly) {
        urlTypeStr = 'query string';
      } else if (urlType.hasProtocol) {
        urlTypeStr = 'full URL';
      } else if (urlType.isRelativeUrl) {
        urlTypeStr = 'relative URL';
      }

      this.debugLog(`Enhanced ${urlTypeStr}: ${originalValue} -> ${newValue}`);

      this.debugLog(`Enhanced attribute ${attributeName} with tab state`, {
        element,
        attributeName,
        tabId,
        before: originalValue,
        after: element.getAttribute(attributeName),
      });
    } catch (error) {
      this.debugLog(`Error enhancing attribute ${attributeName}: ${error}`);
    }
  }

  /**
   * Validates that slotted content has required attributes and matching pairs
   */
  private validateSlottedContent(): void {
    // Track IDs for matching
    const tabIds = new Set<string>();
    const panelIds = new Set<string>();
    const controlledPanelIds = new Set<string>();

    this.debugLog('Starting validation of slotted content');
    this.debugLog('Found tabs:', this.tabs);
    this.debugLog('Found panels:', this.panels);

    // Validate tabs
    this.tabs.forEach(tab => {
      const id = tab.id;
      const controlsId = tab.getAttribute('aria-controls');

      this.debugLog(`Validating tab: id=${id}, controls=${controlsId}`);

      // Check required attributes
      if (!id || !controlsId) {
        console.warn(
          'Tab button missing required attributes (id and/or aria-controls):',
          tab,
          '\nParent element:',
          this
        );
        return;
      }

      // Check for duplicate tab IDs
      if (tabIds.has(id)) {
        console.warn(
          `Duplicate tab ID found: "${id}". Tab IDs must be unique:`,
          tab,
          '\nParent element:',
          this
        );
      }
      tabIds.add(id);
      controlledPanelIds.add(controlsId);

      // Check if controlled panel exists
      const panel = this.querySelector(
        `[slot="panels"][role="tabpanel"]#${controlsId}`
      );
      if (!panel) {
        console.warn(
          `Tab references non-existent panel ID: "${controlsId}":`,
          tab,
          '\nParent element:',
          this
        );
      }
    });

    // Validate panels
    this.panels.forEach(panel => {
      const id = panel.id;
      const role = panel.getAttribute('role');

      this.debugLog(`Validating panel: id=${id}, role=${role}`);

      // Check required attributes
      if (!id || !role) {
        console.warn(
          'Panel missing required attributes (id and/or role="tabpanel"):',
          panel,
          '\nParent element:',
          this
        );
        return;
      }

      // Check for duplicate panel IDs
      if (panelIds.has(id)) {
        console.warn(
          `Duplicate panel ID found: "${id}". Panel IDs must be unique:`,
          panel,
          '\nParent element:',
          this
        );
      }
      panelIds.add(id);

      // Check if panel has a controlling tab
      if (!this.querySelector(`[aria-controls="${id}"]`)) {
        console.warn(
          `Panel with ID "${id}" has no controlling tab:`,
          panel,
          '\nParent element:',
          this
        );
      }
    });

    this.debugLog('Validation complete');
    this.debugLog('- Tab IDs:', Array.from(tabIds));
    this.debugLog('- Panel IDs:', Array.from(panelIds));
    this.debugLog('- Controlled Panel IDs:', Array.from(controlledPanelIds));
  }

  /**
   * Document click handler to close dropdown when clicking outside
   */
  private handleDocumentClick = (event: MouseEvent): void => {
    if (!this.isDropdownOpen) return;

    const target = event.target as HTMLElement;
    const dropdown = this.shadowRoot?.querySelector(
      '.tablist-dropdown-container'
    );

    // Check if click is outside the dropdown
    if (dropdown && !dropdown.contains(target) && !this.contains(target)) {
      this.closeDropdown();
    }
  };

  /**
   * Lifecycle callback when element is added to the DOM.
   * Binds event handlers and sets up keydown listener.
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.debugLog('Component connected to DOM', {
      useUrlState: this.useUrlState,
      enhanceUrls: this.enhanceUrls,
      linkSelector: this.linkSelector,
      globalLinkScope: this.globalLinkScope,
    });

    // Set CSS variables for fade properties
    this.style.setProperty('--fade-color', this.fadeColor);
    this.style.setProperty('--fade-width', this.fadeWidth);

    // Add keydown event listener
    this.addEventListener('keydown', this.boundHandlers.keyDown);

    // Add document click listener for dropdown
    if (this.isDropdownVariation()) {
      document.addEventListener('click', this.boundHandlers.documentClick);
    }

    // Setup responsive listener for responsive variations
    if (this.responsiveVariation) {
      const mediaQuery = window.matchMedia('(max-width: 992px)');
      mediaQuery.addEventListener(
        'change',
        this.boundHandlers.mediaQueryChange
      );
    }

    // Parse selectors on connection
    this.parseSelectors();

    // Set up observer for links if URL state is enabled
    if (this.useUrlState) {
      this.debugLog(
        'URL state is enabled, setting up link observer and enhancing links',
        {
          enhanceUrls: this.enhanceUrls,
        }
      );

      if (this.enhanceUrls) {
        this.debugLog(
          'Setting up link observer because both useUrlState and enhanceUrls are true'
        );
        this.setupLinkObserver();

        // Initial enhancement of existing links
        this.debugLog('Performing initial link enhancement');
        this.enhanceLinks();
      } else {
        this.debugLog(
          'Not setting up link observer or enhancing links because enhanceUrls is false'
        );
      }
    } else {
      this.debugLog(
        'URL state is disabled, skipping link observer and enhancement'
      );
    }

    // Set up resize observer to check for overflow when window size changes
    this.setupResizeObserver();
  }

  /**
   * Lifecycle callback when element is removed from the DOM.
   * Removes event listeners and cleans up state.
   */
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.debugLog('Component disconnected from DOM');
    this.removeEventListener('keydown', this.boundHandlers.keyDown);
    if (this.linkObserver) {
      this.linkObserver.disconnect();
      this.linkObserver = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    // Clean up scroll event listener if it exists
    if (this.tablistElement) {
      this.tablistElement.removeEventListener(
        'scroll',
        this.boundHandlers.tablistScroll
      );
    }
    // Remove document click listener for dropdown
    if (this.isDropdownVariation()) {
      document.removeEventListener('click', this.boundHandlers.documentClick);
    }

    // Remove media query listener
    if (this.responsiveVariation) {
      const mediaQuery = window.matchMedia('(max-width: 992px)');
      mediaQuery.removeEventListener(
        'change',
        this.boundHandlers.mediaQueryChange
      );
    }

    this.cleanup();
  }

  /**
   * Removes event listeners and resets component state.
   * Called during cleanup and before reinitializing.
   * @private
   */
  private cleanup(): void {
    this.debugLog('Cleaning up component state');
    // Remove all event listeners from current tabs
    this.tabs.forEach(tab => {
      if (this.elementsWithListeners.has(tab)) {
        tab.removeEventListener('click', this.boundHandlers.tabClick);
        this.debugLog(`Removed click handler from tab: ${tab.id}`);
      }
    });
    // Clear tracking map
    this.elementsWithListeners = new WeakMap();
    // Clear arrays
    this.tabs = [];
    this.panels = [];
    this.firstTab = null;
    this.lastTab = null;
  }

  /**
   * Lifecycle callback after first render.
   * Initializes tabs and sets up initial state.
   */
  firstUpdated(): void {
    this.debugLog('First update, initializing tabs', {
      activeTabId: this.activeTabId,
    });
    this.initializeTabs();
    this.setupTablistScrollHandling();

    // After initial setup, ensure tablist overflow is checked
    setTimeout(() => {
      this.checkTablistOverflow();
    }, 100);
  }

  /**
   * Handles slot change events for both tabs and panels slots.
   * Reinitializes the component when slotted content changes.
   * @private
   */
  private handleSlotChange = (): void => {
    this.debugLog('Slot content changed, reinitializing', {
      activeTabId: this.activeTabId,
    });

    // Store the current active tab ID
    const currentActiveTabId = this.activeTabId;

    // Clean up existing state
    this.cleanup();

    // Reinitialize with new content
    this.initializeTabs();

    // If we had an active tab and it's not restored yet, try to restore it
    if (currentActiveTabId && this.activeTabId !== currentActiveTabId) {
      const activeTab = this.tabs.find(tab => tab.id === currentActiveTabId);
      if (activeTab) {
        this.debugLog(
          `Restoring active tab after slot change: ${currentActiveTabId}`
        );
        this.setSelectedTab(activeTab, false);
      }
    }
  };

  /**
   * Initializes the tabs component by:
   * - Querying and storing tab and panel elements
   * - Setting up event listeners
   * - Validating content structure
   * - Setting initial state
   * @private
   */
  private initializeTabs(): void {
    this.debugLog('Starting initialization', {
      useUrlState: this.useUrlState,
      enhanceUrls: this.enhanceUrls,
    });

    // Get all tab elements from the tabs slot
    this.tabs = Array.from(this.querySelectorAll('button[slot="tabs"]'));
    this.debugLog('Found tabs:', this.tabs);

    // Get all panel elements from the panels slot
    this.panels = Array.from(
      this.querySelectorAll('[slot="panels"][role="tabpanel"]')
    );
    this.debugLog('Found panels:', this.panels);

    // Set first and last tabs
    this.firstTab = this.tabs[0] || null;
    this.lastTab = this.tabs[this.tabs.length - 1] || null;

    // Validate slotted content
    this.validateSlottedContent();

    // Initialize all tabs and panels with default state
    this.tabs.forEach(tab => {
      // Remove any pre-existing state attributes
      tab.removeAttribute('aria-selected');
      tab.removeAttribute('tabindex');
      if (!tab.hasAttribute('role')) {
        tab.setAttribute('role', 'tab');
      }

      // Add click handler
      tab.addEventListener('click', this.boundHandlers.tabClick);
      this.elementsWithListeners.set(tab, true);
      this.debugLog(`Initialized tab: ${tab.id}`);
    });

    this.panels.forEach(panel => {
      // Remove any pre-existing state attributes
      panel.removeAttribute('hidden');
      panel.removeAttribute('tabindex');
      this.debugLog(`Initialized panel: ${panel.id}`);
    });

    // Check URL for active tab if URL state is enabled
    if (this.useUrlState) {
      this.debugLog('URL state is enabled, checking for active tab in URL');
      const activeTabId = this.getActiveTabFromUrl();
      if (activeTabId) {
        const activeTab = this.tabs.find(tab => tab.id === activeTabId);
        if (activeTab) {
          this.debugLog(`Setting active tab from URL: ${activeTabId}`);
          this.setSelectedTab(activeTab, false);

          // Check if we should enhance links after setting the active tab
          if (this.enhanceUrls) {
            this.debugLog('Enhancing links after setting active tab from URL');
            this.enhanceLinks();
          } else {
            this.debugLog('Not enhancing links because enhanceUrls is false');
          }
          return;
        }
      }
    }

    // Check if we have a stored active tab ID from previous state
    if (this.activeTabId) {
      const activeTab = this.tabs.find(tab => tab.id === this.activeTabId);
      if (activeTab) {
        this.debugLog(`Restoring active tab from state: ${this.activeTabId}`);
        this.setSelectedTab(activeTab, false);
        return;
      }
    }

    // Set the first tab as active if no URL state, stored state, or invalid tab ID
    if (this.firstTab) {
      this.debugLog('Setting first tab as active:', this.firstTab.id);
      this.setSelectedTab(this.firstTab, false);

      // Initialize the selectedTabLabel with the first tab's text for dropdown variations
      if (this.isDropdownVariation() && this.firstTab) {
        this.selectedTabLabel = this.firstTab.textContent?.trim() || '';
        this.debugLog(
          `Setting initial selected tab label: ${this.selectedTabLabel}`
        );
      }

      // Check if we should enhance links after setting the first tab as active
      if (this.useUrlState && this.enhanceUrls) {
        this.debugLog('Enhancing links after setting first tab as active');
        this.enhanceLinks();
      } else {
        this.debugLog('Not enhancing links after setting first tab as active', {
          useUrlState: this.useUrlState,
          enhanceUrls: this.enhanceUrls,
        });
      }
    }
  }

  /**
   * Handles click events on tab elements.
   * @param event - The click event
   * @private
   */
  private handleTabClick = (event: Event) => {
    const tab = event.currentTarget as HTMLElement;
    this.debugLog(`Tab clicked: ${tab.id}`);
    this.setSelectedTab(tab, true);

    // Close dropdown if this is a dropdown variation
    if (this.isDropdownVariation()) {
      this.isDropdownOpen = false;
    }
  };

  /**
   * Toggle dropdown display for mobile dropdown variations
   */
  private toggleDropdown = () => {
    this.isDropdownOpen = !this.isDropdownOpen;
  };

  /**
   * Close the dropdown
   */
  private closeDropdown = () => {
    this.isDropdownOpen = false;
  };

  /**
   * Handle keyboard events for the dropdown toggle
   */
  private handleDropdownKeyDown = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement;

    if (target.classList.contains('dropdown-toggle')) {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.toggleDropdown();
          break;
        case 'Escape':
          event.preventDefault();
          this.closeDropdown();
          break;
        case 'ArrowDown':
          if (!this.isDropdownOpen) {
            event.preventDefault();
            this.isDropdownOpen = true;
          } else if (this.tabs.length > 0) {
            event.preventDefault();
            const firstTab = this.tabs[0];
            if (firstTab) {
              firstTab.focus();
            }
          }
          break;
      }
    } else if (this.isDropdownOpen && target.getAttribute('slot') === 'tabs') {
      // Define variables outside switch statement to fix linter errors
      let dropdownToggle: HTMLElement | null = null;
      let prevTab: HTMLElement | null = null;
      let nextTab: HTMLElement | null = null;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          this.closeDropdown();
          dropdownToggle = this.shadowRoot?.querySelector(
            '.dropdown-toggle'
          ) as HTMLElement;
          if (dropdownToggle) {
            dropdownToggle.focus();
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          prevTab =
            this.tabs[this.tabs.indexOf(target as HTMLElement) - 1] ||
            this.tabs[this.tabs.length - 1];
          if (prevTab) {
            prevTab.focus();
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          nextTab =
            this.tabs[this.tabs.indexOf(target as HTMLElement) + 1] ||
            this.tabs[0];
          if (nextTab) {
            nextTab.focus();
          }
          break;
        case 'Home':
          event.preventDefault();
          if (this.tabs.length > 0) {
            this.tabs[0].focus();
          }
          break;
        case 'End':
          event.preventDefault();
          if (this.tabs.length > 0) {
            this.tabs[this.tabs.length - 1].focus();
          }
          break;
      }
    }
  };

  /**
   * Checks if the current variation uses a dropdown on mobile
   */
  private isDropdownVariation(): boolean {
    if (this.responsiveVariation) {
      // Check if we're on mobile viewport
      const mobileViewport = window.matchMedia(`(max-width: 992px)`).matches;
      return (
        mobileViewport &&
        (this.variation === 'default' || this.variation === 'primary')
      );
    }
    return this.variation === 'default' || this.variation === 'primary';
  }

  /**
   * Handles keyboard navigation between tabs.
   * Supports Left/Right arrows for previous/next and Home/End for first/last.
   * @param event - The keyboard event
   * @private
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement;
    if (!target.matches('[role="tab"]')) return;
    const currentTab = target;
    let newTab: HTMLElement | null = null;
    switch (event.key) {
      case 'ArrowLeft':
        newTab =
          currentTab === this.firstTab
            ? this.lastTab
            : this.tabs[this.tabs.indexOf(currentTab) - 1];
        break;
      case 'ArrowRight':
        newTab =
          currentTab === this.lastTab
            ? this.firstTab
            : this.tabs[this.tabs.indexOf(currentTab) + 1];
        break;
      case 'Home':
        newTab = this.firstTab;
        break;
      case 'End':
        newTab = this.lastTab;
        break;
      default:
        return;
    }
    if (newTab) {
      event.preventDefault();
      this.setSelectedTab(newTab);
    }
  };

  /**
   * Set up the tablist scroll handling
   * @private
   */
  private setupTablistScrollHandling(): void {
    this.tablistElement = this.shadowRoot?.querySelector('.tablist') || null;

    if (this.tablistElement) {
      this.debugLog('Setting up tablist scroll handling');
      this.tablistElement.addEventListener(
        'scroll',
        this.boundHandlers.tablistScroll
      );

      // Initial check for overflow
      this.checkTablistOverflow();
    }
  }

  /**
   * Set up resize observer to check for overflow when window size changes
   * @private
   */
  private resizeObserver: ResizeObserver | null = null;
  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.checkTablistOverflow();
    });

    // Observe the tablist element once it's available
    const setupObserver = () => {
      if (this.tablistElement) {
        this.resizeObserver?.observe(this.tablistElement);
      } else {
        // If element isn't available yet, try again soon
        setTimeout(setupObserver, 100);
      }
    };

    setupObserver();
  }

  /**
   * Handles scroll events on the tablist
   * @private
   */
  private handleTablistScroll = (): void => {
    if (this.isScrolling) return;

    this.isScrolling = true;
    this.checkTablistOverflow();

    // Clear previous timeout if it exists
    if (this.scrollTimeout !== null) {
      window.clearTimeout(this.scrollTimeout);
    }

    // Set timeout to reset scrolling flag
    this.scrollTimeout = window.setTimeout(() => {
      this.isScrolling = false;
    }, 100);
  };

  /**
   * Checks if the tablist has overflow and updates CSS classes accordingly
   * @private
   */
  private checkTablistOverflow(): void {
    if (!this.tablistElement) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.tablistElement;
    const hasOverflowRight = scrollLeft + clientWidth < scrollWidth;
    const hasOverflowLeft = scrollLeft > 0;

    this.debugLog('Tablist overflow check:', {
      hasOverflowLeft,
      hasOverflowRight,
      scrollLeft,
      scrollWidth,
      clientWidth,
    });

    // Find fade elements in the shadow DOM
    const fadeLeft = this.shadowRoot?.querySelector('.fade-left');
    const fadeRight = this.shadowRoot?.querySelector('.fade-right');

    // Toggle visibility based on overflow state
    if (fadeLeft) {
      fadeLeft.classList.toggle('visible', hasOverflowLeft);
    }

    if (fadeRight) {
      fadeRight.classList.toggle('visible', hasOverflowRight);
    }

    // The custom properties are now set in the CSS using variables
    // instead of being set inline here
  }

  /**
   * Handle media query change events
   */
  private handleMediaQueryChange = (event: MediaQueryListEvent): void => {
    // We don't need to find the selected tab since we store it in state
    this.debugLog('Media query changed, updating layout', {
      matches: event.matches,
      activeTabId: this.activeTabId,
    });

    // Trigger a re-render to update layout based on viewport size
    this.requestUpdate();

    // After re-render, ensure tablist overflow is checked
    setTimeout(() => {
      this.checkTablistOverflow();
    }, 100);
  };

  /**
   * Bound event handlers to allow for proper cleanup
   * @private
   */
  private boundHandlers = {
    tabClick: this.handleTabClick,
    keyDown: this.handleKeyDown,
    slotChange: this.handleSlotChange,
    tablistScroll: this.handleTablistScroll,
    dropdownKeyDown: this.handleDropdownKeyDown,
    documentClick: this.handleDocumentClick,
    mediaQueryChange: this.handleMediaQueryChange,
  };

  /**
   * Sets the selected state for a tab and its associated panel.
   * Updates ARIA attributes and visibility.
   * @param tab - The tab element to select
   * @param shouldFocus - Whether to focus the tab after selection
   * @private
   */
  private setSelectedTab(tab: HTMLElement, shouldFocus = true): void {
    this.debugLog(
      `Setting selected tab: ${tab.id}, shouldFocus: ${shouldFocus}`,
      {
        useUrlState: this.useUrlState,
        enhanceUrls: this.enhanceUrls,
      }
    );

    // Store the active tab ID in state
    this.activeTabId = tab.id;

    const panelId = tab.getAttribute('aria-controls');
    const panel = this.querySelector(
      `[slot="panels"][role="tabpanel"]#${panelId}`
    );

    this.debugLog(`Looking for panel with ID: ${panelId}`);
    this.debugLog('Found panel:', panel);

    this.tabs.forEach(t => {
      const isSelected = t === tab;
      t.setAttribute('aria-selected', isSelected.toString());
      t.tabIndex = isSelected ? 0 : -1;

      // Update selectedTabLabel if selected
      if (isSelected) {
        this.selectedTabLabel = t.textContent?.trim() || '';
      }

      this.debugLog(`Tab ${t.id} - selected: ${isSelected}`);
    });

    this.panels.forEach(p => {
      const isSelected = p === panel;
      this.debugLog(`Panel ${p.id} - selected: ${isSelected}`);
      this.debugLog(
        `Panel ${p.id} - before hidden: ${p.hasAttribute('hidden')}`
      );

      if (isSelected) {
        p.removeAttribute('hidden');
        p.tabIndex = 0;
      } else {
        p.setAttribute('hidden', '');
        p.tabIndex = -1;
      }

      this.debugLog(
        `Panel ${p.id} - after hidden: ${p.hasAttribute('hidden')}`
      );
    });

    // Scroll tab into view if it's not fully visible
    if (this.tablistElement && tab && this.variation === 'search') {
      this.scrollTabIntoView(tab);
    }

    // Update URL state if enabled
    if (this.useUrlState) {
      this.debugLog(`Updating URL state with tab: ${tab.id}`);
      this.updateUrlState(tab.id);

      // Check if we should enhance links after updating URL state
      if (this.enhanceUrls) {
        this.debugLog('Enhancing links after updating URL state');
        this.enhanceLinks();
      } else {
        this.debugLog(
          'Not enhancing links after updating URL state because enhanceUrls is false'
        );
      }
    } else {
      this.debugLog('Not updating URL state because useUrlState is false');
    }

    if (shouldFocus) {
      tab.focus();
      this.debugLog(`Dispatching tab-change event for tab: ${tab.id}`);
      this.dispatchEvent(
        new CustomEvent('tab-change', {
          detail: { tabId: tab.id },
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  /**
   * Scrolls a tab into view if it's not fully visible
   * @param tab - The tab element to scroll into view
   * @private
   */
  private scrollTabIntoView(tab: HTMLElement): void {
    if (!this.tablistElement) return;

    const tabRect = tab.getBoundingClientRect();
    const tablistRect = this.tablistElement.getBoundingClientRect();

    // Check if tab is not fully visible
    const isTabFullyVisible =
      tabRect.left >= tablistRect.left && tabRect.right <= tablistRect.right;

    if (!isTabFullyVisible) {
      this.debugLog('Scrolling tab into view:', tab.id);

      const scrollLeft =
        tab.offsetLeft -
        (this.tablistElement.clientWidth - tab.offsetWidth) / 2;

      this.tablistElement.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });

      // Update overflow indicators after scrolling
      setTimeout(() => this.checkTablistOverflow(), 300);
    }
  }

  /**
   * Renders the component's DOM structure.
   * Creates tablist and panels containers with slots for content.
   * @returns The component's template
   */
  render(): TemplateResult {
    const classes = classMap({
      'tabs': true,
      'tabs-mobile-only': this.tabsMobileOnly,
      'tabs-variation-search': this.variation === 'search',
      'tabs-variation-default': this.variation === 'default',
      'tabs-variation-primary': this.variation === 'primary',
    });

    return html`
      <div class="${classes}">
        ${this.isDropdownVariation()
          ? html`
              <!-- Mobile dropdown for dropdown variations -->
              <div
                class="tablist-dropdown-container"
                @keydown="${this.boundHandlers.dropdownKeyDown}"
              >
                <button
                  class="dropdown-toggle ${this.isDropdownOpen ? 'open' : ''}"
                  @click="${this.toggleDropdown}"
                  aria-haspopup="true"
                  aria-expanded="${this.isDropdownOpen}"
                >
                  <span>${this.selectedTabLabel}</span>
                  <span class="dropdown-icon"></span>
                </button>
                <div class="dropdown-menu ${this.isDropdownOpen ? 'open' : ''}">
                  <slot
                    name="tabs"
                    class="dropdown-tabs-slot"
                    @slotchange=${this.boundHandlers.slotChange}
                  ></slot>
                </div>
              </div>

              <!-- Desktop regular tabs for dropdown variations -->
              <div class="tablist-container desktop-only">
                <div class="fade-left"></div>
                <div class="tablist" role="tablist">
                  <slot
                    name="tabs"
                    class="desktop-tabs-slot"
                    @slotchange=${this.boundHandlers.slotChange}
                  ></slot>
                </div>
                <div class="fade-right"></div>
              </div>
            `
          : html`
              <!-- Regular scrolling tabs for search variation -->
              <div class="tablist-container">
                <div class="fade-left"></div>
                <div class="tablist" role="tablist">
                  <slot
                    name="tabs"
                    @slotchange=${this.boundHandlers.slotChange}
                  ></slot>
                </div>
                <div class="fade-right"></div>
              </div>
            `}
        <div class="panels">
          <slot
            name="panels"
            @slotchange=${this.boundHandlers.slotChange}
          ></slot>
        </div>
      </div>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'outline-tabs': OutlineTabs;
  }
}
